import { Link } from "gatsby"
import React from "react"
import tw, { styled } from "twin.macro"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLocationDot, faChevronRight } from "@fortawesome/pro-solid-svg-icons"
import { rewriteSlug } from "../../utils/rewriteSlug"
import Image from "../general/Image"
import { H5 } from "../typography/Typography"
import { PartnerPageStoryblok } from "../../../component-types-sb"
import { useGlobalSettings } from "../../contexts/GlobalSettingsContext"
import { getCityProvinceCountry } from "../../utils/locationHelper"
import usePartnerAttributes from "../../hooks/usePartnerAttributes"
import useLocale from "../../hooks/useLocale"
import CardBase from "./CardBase"

interface CardProps {
  readonly content: PartnerPageStoryblok
  readonly full_slug: string
}

const StyledCardBase = tw(CardBase)`
  max-w-[300px] w-full h-full
  flex flex-col
  p-4 space-y-3
  rounded-lg bg-blue-sky shadow-lg
`

const ImageWrapper = styled.div(() => [
  tw`relative w-full overflow-hidden`,
  {
    ".storyblok-image-wrapper": tw`h-[150px]! rounded-md`,
  },
])

const StyledLink = tw(Link)`
  flex flex-col items-center
  h-full
  text-left
  relative
  transition-card duration-300
`

const LocationText = tw.div`
  text-14 text-black opacity-[50%] w-fit flex items-center
`

const TitleWithHighlight = tw(H5)`
  w-full my-0
  group-hocus:text-blue
  [[data-highlighted="true"].group:hover &, [data-highlighted="true"].group:focus &]:(
    text-body
  )
`

const Description = tw.p`
  w-full text-16 text-black line-clamp-3
`

const PartnerCard: React.FC<CardProps> = ({ content, full_slug }) => {
  const { placeholder_image } = useGlobalSettings()
  const { locale } = useLocale()
  const { cityMap, provinceMap, countryMap } = usePartnerAttributes(locale)

  // Use helpers to extract data
  const locations = Array.isArray(content.location) ? content.location : [content.location]
  const locationStrings = locations
    .filter((location) => location !== null)
    .flatMap((location) => {
      const locationData = getCityProvinceCountry(location ?? "", {cityMap, countryMap, provinceMap})
      return locationData.map(({ city, province, country }) => {
        if (city) return city
        if (province) return province
        return country
      })
    })
    .flat()

  return (
    <StyledLink
      data-highlighted={content.highlight_card}
      className="group"
      to={rewriteSlug(full_slug)}
    >
      <StyledCardBase>
        <ImageWrapper>
          <Image
            image={
              content.studio_image?.filename
                ? content.studio_image
                : placeholder_image
            }
            fluid={464}
          />
        </ImageWrapper>
        <div tw="flex flex-wrap gap-4 items-center">
          {locationStrings.map((locationString, index) => (
            <LocationText key={index}>
              <FontAwesomeIcon icon={faLocationDot} tw="mr-2" />
              {locationString}
            </LocationText>
          ))}
        </div>
        <TitleWithHighlight>{content.name}</TitleWithHighlight>
        {content.description_title && (
          <Description>{content.description_title}</Description>
        )}
        <div tw="flex-grow" />
        <FontAwesomeIcon
          icon={faChevronRight}
          tw="text-black text-18 self-end py-0 pr-4 group-hover:animate-bounce-x"
        />
      </StyledCardBase>
    </StyledLink>
  )
}

export default PartnerCard
